import { useParams, useNavigate } from "react-router-dom"
import { JobbinPrimaryButton } from "../../components/primaryButton"
import { JobbinSecondaryButton } from "../../components/secondaryButton"
import { CandidateDetails } from "../candidate/model"
import { useEffect, useState } from "react"
import * as candidatesService from "../../../src/services/candidates-service"
import { updateApplicationStatus } from "../../services/application-service"
import { UpdateApplicationStatusRequest } from "../candidate/InvitationRequest"
import { JobbinSecondaryButtonRed } from "../../components/secondaryButtonRed"
import { FeedbackRequest } from "./feedbackRequest"
import FeedbackModal from "../../components/feedbackModal"
import { PillItem } from "../../components/pill"
import { CandidateStatsCard } from "../../components/candidateStats"
import { useToast } from "../../context/toast"

const PreferredGigPills = ({
  titles,
  className = "",
}: {
  titles?: string[]
  className?: string
}) => {
  if (!titles || titles.length === 0) return null
  const visible = titles.slice(0, 2)
  const rest = titles.slice(2)
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {visible.map((pozitie, index) => (
        <PillItem key={`${pozitie}-${index}`} text={pozitie} classes="" />
      ))}
      {rest.length > 0 && (
        <div className="group relative" title={rest.join(", ")}>
          <div className="bg-figmaBlack rounded-[100px] h-fit px-[12px] py-[8px] cursor-default">
            <p className="text-white text-[14px]">+{rest.length} poziții</p>
          </div>
          <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 hidden -translate-x-1/2 group-hover:block">
            <div className="max-w-[260px] rounded-xl bg-figmaBlack px-3 py-2 shadow-lg">
              <div className="flex flex-wrap gap-1.5">
                {rest.map((pozitie, index) => (
                  <span
                    key={`${pozitie}-${index}`}
                    className="whitespace-nowrap rounded-full bg-white/15 px-2 py-1 text-[12px] text-white"
                  >
                    {pozitie}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export const ApplicationCandidatePage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { notify } = useToast()

  const [candidateData, setCandidateData] = useState<CandidateDetails>()
  const [feedbackRequest, setFeedbackRequest] = useState<FeedbackRequest>()
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)
  const [isPhotoOpen, setIsPhotoOpen] = useState(false)

  useEffect(() => {
    if (!id) return;  // guard important
    candidatesService
        .getCandidateByApplicationId(id)
        .then((candidate: CandidateDetails) => {
          setCandidateData(candidate);
        });
  }, [id]);  // <-- adaugă id aici

  const rejectCandidate = async () => {
    if (!id) return
    const updateApplicationStatusRequest: UpdateApplicationStatusRequest = {
      applicationStatus: "EMPLOYER_REFUSED",
    }
    const response = await updateApplicationStatus(updateApplicationStatusRequest, id)
    if (response.ok) {
      notify("Candidatul a fost refuzat cu succes!", "success")
      navigate("/candidates")
    } else {
      notify("A apărut o eroare. Te rugăm să contactezi suportul.", "error")
      navigate("/candidates")
    }
  }

  const acceptCandidate = async () => {
    if (!id) return
    const updateApplicationStatusRequest: UpdateApplicationStatusRequest = {
      applicationStatus: "IN_PROGRESS",
    }
    const response = await updateApplicationStatus(updateApplicationStatusRequest, id)
    if (response.ok) {
      notify("Candidatul a fost acceptat cu succes!", "success")
      navigate("/candidates")
    } else {
      notify("A apărut o eroare. Te rugăm să contactezi suportul.", "error")
      navigate("/candidates")
    }
  }

  const endApplication = async () => {
    if (!id || !candidateData) return
    const updateApplicationStatusRequest: UpdateApplicationStatusRequest = {
      applicationStatus: "COMPLETED",
    }
    const response = await updateApplicationStatus(updateApplicationStatusRequest, id)
    if (response.ok) {
      notify("Colaborarea a fost încheiată cu succes!", "success")
      const nextFeedbackRequest: FeedbackRequest = {
        applicationId: candidateData.id,
        description: "",
        rating: 0,
        to: candidateData.candidateId,
        from: candidateData.employerId,
        candidateAttended: true,
      }
      setFeedbackRequest(nextFeedbackRequest)
      setIsFeedbackModalOpen(true)
    } else {
      notify("A apărut o eroare. Te rugăm să contactezi suportul.", "error")
      navigate("/candidates")
    }
  }

  const onPhoneClicked = () => {
    if (!candidateData?.candidatePhoneNumber) return
    window.open(`tel:${candidateData.candidatePhoneNumber}`)
  }

  const onEmailClicked = () => {
    if (!candidateData?.candidateMail) return
    window.open(`mailto:${candidateData.candidateMail}`, "_self")
  }

  const onWhatsAppClicked = () => {
    if (!candidateData?.candidatePhoneNumber) return
    const phone = candidateData.candidatePhoneNumber.replace(/\D/g, '')
    const intlPhone = phone.startsWith('0') ? '40' + phone.slice(1) : phone
    window.open(`https://wa.me/${intlPhone}`, '_blank')
  }

  const contactButtons = () => (
    <div className="mt-4 flex flex-col gap-3 sm:flex-row">
      <div className="flex-1">
        <JobbinSecondaryButton text="Email" onClickAction={onEmailClicked} />
      </div>
      <div className="flex-1">
        <JobbinPrimaryButton text="Telefon" onClickAction={onPhoneClicked} />
      </div>
      {candidateData?.status === "IN_PROGRESS" && candidateData?.candidatePhoneNumber && (
        <div className="flex-1">
          <JobbinSecondaryButton text="WhatsApp" onClickAction={onWhatsAppClicked} />
        </div>
      )}
    </div>
  )

  const getButtons = () => {
    if (!candidateData) return null

    if (candidateData.status === "EXPIRED") {
      return (
        <div id="buttons" className="mt-6 rounded-2xl bg-gray-50 border border-gray-200 px-5 py-4 text-center">
          <p className="text-figmaDarkGray text-sm">Candidatul nu a acceptat invitația în 7 zile</p>
        </div>
      )
    }

    if (candidateData.status === "AWAITING_FEEDBACK") {
      return (
        <div id="buttons" className="mt-6">
          <JobbinPrimaryButton
            text="Lasă feedback"
            onClickAction={() => {
              const nextFeedbackRequest: FeedbackRequest = {
                applicationId: candidateData.id,
                description: "",
                rating: 0,
                to: candidateData.candidateId,
                from: candidateData.employerId,
                candidateAttended: true,
              }
              setFeedbackRequest(nextFeedbackRequest)
              setIsFeedbackModalOpen(true)
            }}
          />
        </div>
      )
    }

    if (candidateData.status === "PENDING") {
      return (
        <div id="buttons" className="mt-6">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="w-full">
              <JobbinSecondaryButton onClickAction={rejectCandidate} text="Refuză candidat" />
            </div>
            {candidateData.type !== "INVITATION" ? (
                <div className="w-full">
                  <JobbinPrimaryButton onClickAction={acceptCandidate} text="Acceptă candidat" />
                </div>
            ) : null}
          </div>
          {contactButtons()}
        </div>
      )
    }

    if (candidateData.status === "CANDIDATE_REFUSED") {
      return (
        <div id="buttons" className="mt-6 rounded-2xl bg-gray-50 border border-gray-200 px-5 py-4 text-center">
          <p className="text-figmaDarkGray text-sm">Candidatul a refuzat invitația</p>
        </div>
      )
    }

    if (candidateData.status === "EMPLOYER_REFUSED") {
      return (
        <div id="buttons" className="mt-6 rounded-2xl bg-gray-50 border border-gray-200 px-5 py-4 text-center">
          <p className="text-figmaDarkGray text-sm">Ai refuzat acest candidat</p>
        </div>
      )
    }

    if (candidateData.status === "COMPLETED") {
      return (
        <div id="buttons" className="mt-6 rounded-2xl bg-gray-50 border border-gray-200 px-5 py-4 text-center">
          <p className="text-figmaDarkGray text-sm">Colaborarea a fost finalizată</p>
        </div>
      )
    }

    return (
      <div id="buttons" className="mt-6">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="w-full">
            <JobbinSecondaryButtonRed onClickAction={rejectCandidate} text="Candidat nepotrivit" />
          </div>
          <div className="w-full">
            <JobbinPrimaryButton onClickAction={endApplication} text="Termină colaborarea" />
          </div>
        </div>
        {contactButtons()}
      </div>
    )
  }

  return (
      <div id="candidate-page" className="w-full px-4 py-4 sm:px-6 sm:py-6">
        {isPhotoOpen && candidateData?.profilePictureUrl && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
            onClick={() => setIsPhotoOpen(false)}
          >
            <img
              src={candidateData.profilePictureUrl}
              alt="Poza candidat"
              className="max-h-[90vh] max-w-[90vw] rounded-2xl object-contain"
            />
          </div>
        )}

        <FeedbackModal
            isOpen={isFeedbackModalOpen}
            closeModal={() => setIsFeedbackModalOpen(false)}
            feedbackData={feedbackRequest}
        />

        {/* Candidate bar */}
        <div
            id="candidate-bar"
            className="bg-white rounded-[16px] p-4 sm:p-6 flex flex-col items-center text-center xl:flex-row xl:items-center xl:justify-between xl:text-left"
            style={{ border: "1px solid #e5e7eb" }}
        >
          {/* Stanga: poza + nume + ocupatie + rating */}
          <div className="flex flex-col items-center text-center xl:flex-row xl:items-center xl:gap-6 xl:text-left">
            <div
              className={`flex h-[110px] w-[110px] mx-auto xl:mx-0 shrink-0 rounded-full bg-figmaLightBg ${candidateData?.profilePictureUrl ? "cursor-zoom-in" : ""}`}
              onClick={() => candidateData?.profilePictureUrl && setIsPhotoOpen(true)}
            >
              <div className="m-auto flex h-[90px] w-[90px] items-center justify-center rounded-full bg-figmaSpecialBlue bg-opacity-25">
                {candidateData?.profilePictureUrl ? (
                    <img
                        src={candidateData.profilePictureUrl}
                        className="h-[90px] w-[90px] rounded-full object-cover"
                        alt={`${candidateData.candidateFirstName} ${candidateData.candidateLastName}`}
                    />
                ) : (
                    <p className="text-lg font-semibold text-figmaBlack">
                      {candidateData?.candidateFirstName?.[0]}
                      {candidateData?.candidateLastName?.[0]}
                    </p>
                )}
              </div>
            </div>

            <div className="mt-3 xl:mt-0 min-w-0">
              <p className="text-[22px] sm:text-[26px] lg:text-[28px] font-semibold text-figmaBlack break-words">
                {candidateData ? `${candidateData.candidateFirstName} ${candidateData.candidateLastName}` : ""}
              </p>

              {/* Mobile: ocupatie + oras, rating separat */}
              <div className="mt-1 flex flex-wrap items-center justify-center xl:hidden gap-2 min-w-0">
                <p className="text-figmaDarkGray text-[14px] break-words min-w-0">
                  {candidateData?.preferredGigTitles?.[0] ?? ""}
                </p>
                {candidateData?.location && (
                  <>
                    <span className="text-figmaDarkGray opacity-40 shrink-0">•</span>
                    <div className="flex items-center gap-1 min-w-0">
                      <img src="/images/navigation.svg" alt="location" className="h-[14px] shrink-0" />
                      <p className="text-figmaDarkGray text-[14px] break-words min-w-0">{candidateData.location}</p>
                    </div>
                  </>
                )}
              </div>
              <div className="mt-2 flex items-center justify-center gap-1 xl:hidden">
                <img src="/images/rate.svg" className="h-fit shrink-0" alt="rating" />
                <p className="text-figmaContrast font-semibold">{candidateData?.candidateRating || ""}</p>
              </div>

              {/* Desktop: ocupatie + rating pe acelasi rand */}
              <div className="hidden xl:flex items-center gap-3 mt-1 min-w-0">
                <p className="text-figmaDarkGray text-[14px] min-w-0 truncate flex-1">
                  {candidateData?.preferredGigTitles?.[0] ?? ""}
                </p>
                <span className="text-figmaDarkGray opacity-40 shrink-0">•</span>
                <div className="flex items-center gap-1 shrink-0">
                  <img src="/images/rate.svg" className="h-fit shrink-0" alt="rating" />
                  <p className="text-figmaContrast font-semibold">{candidateData?.candidateRating || ""}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Dreapta desktop: locatie + pills */}
          <div className="hidden xl:flex xl:items-center xl:gap-4">
            <div className="border-x-2 border-gray-100 px-10 text-center">
              <p className="text-xs text-figmaDarkGray">LOCATIE</p>
              <p className="text-figmaPrimary break-words">{candidateData?.location || ""}</p>
            </div>
            <PreferredGigPills titles={candidateData?.preferredGigTitles} />
          </div>

          {/* Pills mobile */}
          <PreferredGigPills
            titles={candidateData?.preferredGigTitles}
            className="mt-3 justify-center xl:hidden"
          />
        </div>

        {/* Scoruri candidat */}
        <CandidateStatsCard
          className="mt-6"
          rating={candidateData?.candidateRating}
          responseRate={candidateData?.responseRate}
          responseRateShiftCount={candidateData?.responseRateShiftCount}
          attendance={candidateData?.attendance}
          attendanceShiftCount={candidateData?.attendanceShiftCount}
          numberOfRatings={candidateData?.numberOfRatings}
        />

        {/* About */}
        {/* About */}
        <div
            id="about"
            className="mt-6 bg-white rounded-2xl overflow-hidden"
            style={{ border: "1px solid #e5e7eb" }}
        >
          <p className="candidate-feature-title px-5 py-4 border-b border-gray-100">Informatii despre candidat</p>
          <p className="px-5 py-4 text-[14px] leading-6 text-figmaDarkGray break-words">
            {candidateData?.description || ""}
          </p>
          <div className="px-5 pb-4 flex items-center gap-3">
            <img src="/images/resume-icon.svg" alt="resume" className="shrink-0" />

            {candidateData?.cvUrl ? (
                <a
                    href={candidateData.cvUrl}
                    className="underline text-figmaBlack bold-text text-[14px] break-all"
                    target="_blank"
                    rel="noreferrer"
                >
                  {`${candidateData.candidateFirstName} ${candidateData.candidateLastName}`}
                  _cv.pdf
                </a>
            ) : (
                <span className="text-[14px] leading-6 text-figmaDarkGray">
                  Candidatul nu a atașat un CV la această aplicare.
                </span>
            )}
          </div>
      </div>

  {/* Pozitii anterioare + Video */}
  <div
      id="pozitii-anterioare-video-buttons"
      className="mt-6 flex flex-col gap-6 xl:flex-row xl:items-start"
  >
    <div
        id="pozitii-anterioare"
        className="w-full bg-white rounded-2xl overflow-hidden"
        style={{ border: "1px solid #e5e7eb" }}
    >
      <p className="candidate-feature-title px-5 py-4 border-b border-gray-100">
        Poziții anterioare
      </p>
      <div className="flex flex-col p-3 max-h-[280px] overflow-y-auto">
        {candidateData?.completedApplications &&
        candidateData.completedApplications.length !== 0 ? (
            candidateData.completedApplications.map((pozitie, index) => (
                <div
                    key={index}
                    className="px-3 py-4 break-words"
                    style={{
                      borderBottom:
                          index < candidateData.completedApplications.length - 1
                              ? "1px solid #f3f4f6"
                              : "none",
                    }}
                >
                  <p className="bold-text text-[14px] text-figmaBlack">
                    {pozitie.businessName} - {pozitie.gigTitle}
                  </p>
                  <p className="mt-1 text-[14px] leading-6">
                    <span className="text-figmaPrimary">Recenzie:</span>{" "}
                    <span className="text-figmaDarkGray">
                      {pozitie.description?.trim() || "Niciun mesaj lăsat"}
                    </span>
                  </p>
                </div>
            ))
        ) : (
            <p className="px-3 py-4 text-[14px] text-figmaDarkGray">Fara pozitii anterioare</p>
        )}
      </div>
    </div>

    <div id="video-buttons" className="w-full">
      <div
          className="bg-white rounded-2xl overflow-hidden"
          style={{ border: "1px solid #e5e7eb" }}
      >
        <p className="candidate-feature-title px-5 py-4 border-b border-gray-100">Video</p>
        <div className="flex min-h-[220px] w-full items-center justify-center bg-figmaDark text-center text-white">
          {candidateData?.videoUrl ? (
              <video
                  id="movie"
                  className="h-auto max-h-[420px] w-full"
                  src={candidateData.videoUrl}
                  controls
              />
          ) : (
              <p className="px-4 py-8">Fara video</p>
          )}
        </div>
      </div>

      {candidateData ? getButtons() : null}
    </div>
  </div>
</div>
)
}